import { Routes } from '@angular/router';
import { inject } from '@angular/core';
import { authGuard } from './guards/auth.guard';
import { loginGuard } from './guards/login.guard';
import { permissionGuard } from './guards/permission.guard';
import { GARS_PERMISSIONS } from './models/gars-permissions.model';
import { AuthService } from './services/auth.service';

export const routes: Routes = [
  {
    path: 'login',
    loadComponent: () => import('./pages/login/login').then(m => m.LoginComponent),
    canActivate: [loginGuard]
  },
  {
    path: '',
    redirectTo: 'login',
    pathMatch: 'full'
  },
  {
    path: 'app',
    loadComponent: () => import('./components/landing-page/landing-page').then(m => m.LandingPage),
    canActivate: [authGuard],
    children: [
      {
        path: '',
        redirectTo: () => inject(AuthService).getDefaultFeaturePath(),
        pathMatch: 'full'
      },
      {
        path: 'home',
        redirectTo: () => inject(AuthService).getDefaultFeaturePath(),
        pathMatch: 'full'
      },
      {
        path: 'workspace-analytics',
        loadComponent: () => import('./pages/workspace-adoption/workspace-adoption').then(m => m.WorkspaceAdoptionComponent),
        canActivate: [authGuard, permissionGuard],
        data: {
          moduleId: 'dashboard',
          requiredPermission: GARS_PERMISSIONS.WORKSPACE_ADOPTION_VIEW,
        }
      },
      {
        path: 'google-workspace-adoption',
        redirectTo: 'workspace-analytics',
        pathMatch: 'full'
      },
      {
        path: 'license-management',
        loadComponent: () => import('./pages/license-monitoring-dashboard/license-monitoring-dashboard').then(m => m.LicenseMonitoringDashboard),
        canActivate: [authGuard, permissionGuard],
        data: {
          moduleId: 'license-monitoring',
          requiredPermissions: [
            GARS_PERMISSIONS.LICENSE_OVERVIEW_VIEW,
            GARS_PERMISSIONS.LICENSE_DIRECTORY_VIEW,
          ]
        }
      },
      {
        path: 'license-monitoring-dashboard',
        redirectTo: 'license-management',
        pathMatch: 'full'
      },
      {
        path: 'gemini-reports',
        loadComponent: () => import('./pages/gemini-reports/gemini-reports').then(m => m.GeminiReports),
        canActivate: [authGuard, permissionGuard],
        data: {
          moduleId: 'gemini-reports',
          requiredPermissions: [
            GARS_PERMISSIONS.GEMINI_OVERVIEW_VIEW,
            GARS_PERMISSIONS.GEMINI_TELEMETRY_VIEW,
          ]
        }
      },
      {
        path: 'gem-reports',
        loadComponent: () => import('./gemini-gems/gemini-gems').then(m => m.GeminiGems),
        canActivate: [authGuard, permissionGuard],
        data: {
          moduleId: 'gemini-reports',
          requiredPermissions: [
            GARS_PERMISSIONS.GEMINI_OVERVIEW_VIEW,
            GARS_PERMISSIONS.GEMINI_TELEMETRY_VIEW,
          ]
        }
      },
      {
        path: 'gemini-intelligence',
        loadComponent: () => import('./pages/gemini-reports/gemini-reports').then(m => m.GeminiReports),
        canActivate: [authGuard, permissionGuard],
        data: {
          moduleId: 'gemini-reports',
          requiredPermissions: [
            GARS_PERMISSIONS.GEMINI_OVERVIEW_VIEW,
            GARS_PERMISSIONS.GEMINI_TELEMETRY_VIEW,
          ]
        }
      },
      {
        path: 'gemini-adoption',
        loadComponent: () => import('./pages/gemini-adoption/gemini-adoption.component').then(m => m.GeminiAdoptionComponent),
        canActivate: [authGuard, permissionGuard],
        data: {
          moduleId: 'gemini-reports',
          requiredPermissions: [
            GARS_PERMISSIONS.GEMINI_OVERVIEW_VIEW,
            GARS_PERMISSIONS.GEMINI_TELEMETRY_VIEW,
          ]
        }
      },
      {
        path: 'gemini-monitoring',
        loadComponent: () => import('./pages/gemini-monitoring/gemini-monitoring').then(m => m.GeminiMonitoring),
        canActivate: [authGuard, permissionGuard],
        data: {
          moduleId: 'gemini-reports',
          requiredPermissions: [
            GARS_PERMISSIONS.GEMINI_OVERVIEW_VIEW,
            GARS_PERMISSIONS.GEMINI_TELEMETRY_VIEW,
          ]
        }
      },
      {
        path: 'asf-reports',
        loadComponent: () => import('./pages/asf-reports/asf-reports').then(m => m.AsfReports),
        canActivate: [authGuard]
      },
      {
        path: 'feature-matrix',
        loadComponent: () => import('./pages/ai-availability-matrix/ai-availability-matrix').then(m => m.AiAvailabilityMatrix),
        canActivate: [authGuard]
      },
      {
        path: 'ai-availability-matrix',
        redirectTo: 'feature-matrix',
        pathMatch: 'full'
      },
      {
        path: 'role-access',
        loadComponent: () => import('./pages/role-access/role-access').then(m => m.RoleAccess),
        canActivate: [authGuard, permissionGuard],
        data: {
          moduleId: 'settings',
          requiredPermission: GARS_PERMISSIONS.ACCESS_SECURITY_MANAGE
        }
      },
      {
        path: 'access-security',
        loadComponent: () => import('./pages/settings/settings').then(m => m.Settings),
        canActivate: [authGuard, permissionGuard],
        data: {
          moduleId: 'settings',
          requiredPermission: GARS_PERMISSIONS.ACCESS_SECURITY_MANAGE
        }
      },
      {
        path: 'admin-settings',
        redirectTo: 'access-security',
        pathMatch: 'full'
      },
      {
        path: 'cost-avoidance-calculator',
        loadComponent: () => import('./pages/google-site-portal/google-site-portal').then(m => m.GoogleSitePortalComponent),
        canActivate: [authGuard]
      },
      {
        path: 'ai-savings-calculator',
        redirectTo: 'cost-avoidance-calculator',
        pathMatch: 'full'
      },
      {
        path: 'google-site',
        redirectTo: 'cost-avoidance-calculator',
        pathMatch: 'full'
      },
      {
        path: 'google-site-portal',
        redirectTo: 'cost-avoidance-calculator',
        pathMatch: 'full'
      },
    ]
  },
  {
    path: '**',
    redirectTo: 'login'
  }
];
